import { useState } from 'react';
import type { FormEvent } from 'react';
import { useAccountApi } from '../../api/AccountApiProvider';
import { useAccountSession } from '../../auth/AccountSessionProvider';
import { toApiError } from '../../api/errors';
import type { ApiError } from '../../api/errors';
import type { NewTenantInput } from '../../api/types';
import { useI18n } from '../../i18n/useI18n';
import { Modal } from '../../ui/Modal';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { Button } from '../../ui/primitives';
import { TextField } from '../../ui/form';
import { AttributionNotice } from '../../ui/AttributionNotice';
import { useToast } from '../../ui/Toast';
import { checkOwnProvider, ownChoice } from './identityChoice';
import type { OwnProviderErrors, OwnProviderField, OwnProviderForm } from './identityChoice';
import {
  CLUSTER_IDENTITY_NAMESPACE,
  adminGroupRefusal,
  isReservedGroup,
  prescribedAdminGroup,
  reservedComplaint,
  slugTakesThePrescribedName,
  suggestedAdminGroup,
} from './adminGroup';

const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;

type Field = 'slug' | 'display_name' | 'version' | 'admin_group';
type Values = Record<Field, string>;
type Errors = Partial<Record<Field, string>>;

const EMPTY: Values = {
  slug: '',
  display_name: '',
  version: '',
  admin_group: '',
};

const NO_PROVIDER: OwnProviderForm = {
  issuer: '',
  client_id: '',
  audience: '',
  signing_algs: [],
};

/**
 * Declaring a tenancy.
 *
 * No identity provider is asked for, and the omission is the order of things
 * rather than a simplification: a tenancy is declared, its representative signs
 * in to the account, and *they* choose how their people will sign in. The
 * cluster stands up on the provider's own identity provider meanwhile, so it is
 * reachable before its owner has been asked anything.
 *
 * That has one edge, and this dialog handles it where it appears rather than
 * pretending it does not exist: a deployment that runs no identity provider of
 * its own cannot stand a cluster up on one, and refuses the declaration with
 * `provider_identity_unavailable`. There is nothing in a listing that says in
 * advance whether this deployment is such a one — so the form asks, and if the
 * answer is that refusal it opens the fields for the customer's own provider
 * and says why they appeared.
 *
 * The administrator group is asked for and cannot be changed afterwards. It is
 * substituted into the Cedar policy the cluster is seeded with, the store is
 * authoritative from the first start, and the control plane cannot rewrite it.
 * Which is why the name it suggests is derived from the slug rather than fixed
 * in a dictionary, why a name inside the cluster's own namespace is refused
 * here as well as at the edge, and why the one slug that spoils the prescribed
 * name says so while the slug is being typed — see ./adminGroup.ts.
 */
export function CreateTenantDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useI18n();
  const api = useAccountApi();
  const { session } = useAccountSession();
  const toast = useToast();

  const [values, setValues] = useState<Values>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [provider, setProvider] = useState<OwnProviderForm>(NO_PROVIDER);
  const [providerErrors, setProviderErrors] = useState<OwnProviderErrors>({});
  /**
   * What the *provider* refused, keyed by the box the value came from.
   *
   * Kept apart from `providerErrors` because these are the server's own
   * sentences rather than keys of this dictionary: they name the address that
   * was dialled and the date a certificate ran out on, and neither is something
   * this console could compose. The checks above run first and win, so a box
   * that is wrong on its face says so in the reader's language.
   */
  const [providerSaid, setProviderSaid] = useState<Partial<Record<OwnProviderField, string>>>({});
  /** Set when the deployment turns out to run no identity provider of its own. */
  const [providerRequired, setProviderRequired] = useState(false);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const set = (field: Field) => (event: { target: { value: string } }) => {
    setValues((current) => ({ ...current, [field]: event.target.value }));
  };

  /**
   * The name to offer, derived from the slug already typed rather than fixed in
   * the dictionary: a suggestion that is merely valid teaches a shape, one that
   * follows the slug is the name this tenancy should actually be given. Before a
   * slug is typed — and for the slug whose prescribed name is refused — there is
   * none, and the dictionary's sample stands in as a sample.
   */
  const suggested = suggestedAdminGroup(values.slug);

  function validate(): Errors {
    const found: Errors = {};
    if (values.display_name.trim() === '') found.display_name = t('createTenant.check.nameRequired');

    if (values.slug.trim() === '') found.slug = t('createTenant.check.slugRequired');
    else if (!SLUG.test(values.slug.trim())) found.slug = t('createTenant.check.slugFormat');


    if (values.version.trim() === '') found.version = t('createTenant.check.versionRequired');

    // The namespace rule is the server's, and the server applies it again. It
    // is repeated here because this box cannot be corrected after the tenancy
    // exists, so the round trip is worth saving.
    if (values.admin_group.trim() === '')
      found.admin_group = t('createTenant.check.adminGroupRequired');
    else if (isReservedGroup(values.admin_group)) found.admin_group = sayReserved();

    return found;
  }

  /** A name inside the cluster's own namespace, in the reader's language. */
  function sayReserved(): string {
    const complaint = reservedComplaint(values.slug);
    return t(complaint.key, complaint.params);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();

    const found = validate();
    const providerFound = providerRequired ? checkOwnProvider(provider) : {};
    setErrors(found);
    setProviderErrors(providerFound);
    setProviderSaid({});
    setFailure(null);
    if (Object.keys(found).length > 0 || Object.keys(providerFound).length > 0) return;

    const input: NewTenantInput = {
      slug: values.slug.trim(),
      display_name: values.display_name.trim(),
      version: values.version.trim(),
      admin_group: values.admin_group.trim(),
      ...(providerRequired ? { identity: ownChoice(provider) } : {}),
    };

    setSubmitting(true);
    try {
      const created = await api.provider.createTenant(input);
      toast(t('toast.tenantDeclared', { name: created.display_name }));
      onCreated();
    } catch (error: unknown) {
      const failed = toApiError(error);
      if (failed.code === 'provider_identity_unavailable') {
        setProviderRequired(true);
        setFailure(failed);
      } else if (failed.code === 'identity_provider_refused') {
        // The provider was asked and would not carry a cluster. The alert says
        // which answer refused; the box the value came from is named by the
        // server, and never guessed here from the code.
        setProviderSaid(providerFieldsOf(failed));
        setFailure(failed);
      } else if (failed.code === 'invalid_request') {
        // The server names the box; the console never guesses it from a code.
        const named = fieldsOf(failed);
        // One refusal it can say better than the server can: the server's
        // sentence is English, and this console knows both the rule and what it
        // sent, so it does not have to read prose to recognise it.
        const group = adminGroupRefusal(failed, input);
        if (group !== null) {
          named.admin_group = group.known
            ? t(group.complaint.key, group.complaint.params)
            : group.reason;
        }
        if (Object.keys(named).length > 0) setErrors(named);
        else setFailure(failed);
      } else {
        setFailure(failed);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={t('createTenant.title')}
      subtitle={t('createTenant.subtitle')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <span className="spacer" />
          <Button variant="primary" form="create-tenant" type="submit" disabled={submitting}>
            {submitting ? `${t('createTenant.submitting')}…` : t('createTenant.submit')}
          </Button>
        </>
      }
    >
      {failure !== null && (
        <div style={{ marginBottom: 18 }}>
          <DeclareFailure failure={failure} />
        </div>
      )}

      <form id="create-tenant" onSubmit={(event) => void onSubmit(event)} noValidate>
        <TextField
          label={t('createTenant.name.label')}
          placeholder={t('createTenant.name.placeholder')}
          value={values.display_name}
          onChange={set('display_name')}
          {...(errors.display_name === undefined ? {} : { error: errors.display_name })}
        />
        <TextField
          label={t('createTenant.slug.label')}
          hint={t('createTenant.slug.hint')}
          placeholder={t('createTenant.slug.placeholder')}
          mono
          value={values.slug}
          onChange={set('slug')}
          {...(errors.slug === undefined ? {} : { error: errors.slug })}
        />
        {/* There is no box for the tenancy's names, so the rule that makes
            them is stated where the box used to be — otherwise the first place
            somebody learns them is the report of a reconcile. */}
        <div style={{ marginBottom: 18 }}>
          <Alert tone="neutral" title={t('createTenant.names.title')}>
            <p>{t('createTenant.names.body')}</p>
          </Alert>
        </div>
        <TextField
          label={t('createTenant.version.label')}
          hint={t('createTenant.version.hint')}
          placeholder={t('createTenant.version.placeholder')}
          mono
          value={values.version}
          onChange={set('version')}
          {...(errors.version === undefined ? {} : { error: errors.version })}
        />
        <TextField
          label={t('createTenant.adminGroup.label')}
          hint={t('createTenant.adminGroup.hint', { namespace: CLUSTER_IDENTITY_NAMESPACE })}
          placeholder={suggested ?? t('createTenant.adminGroup.placeholder')}
          mono
          value={values.admin_group}
          onChange={set('admin_group')}
          {...(errors.admin_group === undefined ? {} : { error: errors.admin_group })}
        />

        {slugTakesThePrescribedName(values.slug) && (
          <div style={{ marginBottom: 18 }}>
            <Alert tone="warn" title={t('createTenant.adminGroup.slugTaken.title')}>
              {t('createTenant.adminGroup.slugTaken.body', {
                namespace: CLUSTER_IDENTITY_NAMESPACE,
                name: prescribedAdminGroup(values.slug),
              })}
            </Alert>
          </div>
        )}

        {providerRequired && (
          <>
            <p className="section-title">{t('createTenant.provider.section')}</p>
            <TextField
              label={t('identityProvider.field.issuer')}
              hint={t('identityProvider.issuer.hint')}
              placeholder={t('identityProvider.issuer.placeholder')}
              type="url"
              mono
              value={provider.issuer}
              onChange={(event) => {
                setProvider({ ...provider, issuer: event.target.value });
              }}
              {...providerComplaint(
                providerErrors.issuer === undefined ? undefined : t(providerErrors.issuer),
                providerSaid.issuer,
              )}
            />
            <TextField
              label={t('identityProvider.field.clientId')}
              hint={t('identityProvider.clientId.hint')}
              placeholder={t('identityProvider.clientId.placeholder')}
              mono
              value={provider.client_id}
              onChange={(event) => {
                setProvider({ ...provider, client_id: event.target.value });
              }}
              {...providerComplaint(
                providerErrors.client_id === undefined ? undefined : t(providerErrors.client_id),
                providerSaid.client_id,
              )}
            />
            <TextField
              label={t('identityProvider.field.audience')}
              hint={t('identityProvider.audience.hint')}
              mono
              value={provider.audience}
              onChange={(event) => {
                setProvider({ ...provider, audience: event.target.value });
              }}
              {...providerComplaint(
                providerErrors.audience === undefined ? undefined : t(providerErrors.audience),
                providerSaid.audience,
              )}
            />
          </>
        )}
      </form>

      <div className="stack" style={{ marginTop: 18 }}>
        {!providerRequired && (
          <Alert tone="neutral" title={t('createTenant.identity.title')}>
            {t('createTenant.identity.body')}
          </Alert>
        )}
        <Alert tone="neutral" title={t('createTenant.secrets.title')}>
          {t('createTenant.secrets.body')}
        </Alert>
        {session !== null && <AttributionNotice actorName={session.actor.audit_name} />}
      </div>
    </Modal>
  );
}

/**
 * What one provider box shows: this console's own check first, and what the
 * provider said where there is no such check to make.
 */
function providerComplaint(checked?: string, said?: string): { error?: string } {
  const complaint = checked ?? said;
  return complaint === undefined ? {} : { error: complaint };
}

/**
 * The provider's own complaints, keyed by the box they are about.
 *
 * The wire names them under `identity.`, because that is where they sit in the
 * body; the boxes on this form are the bare names. Nothing is derived from the
 * failure code — the server names the field, and it is the only side that knows
 * which value its answer was about.
 */
function providerFieldsOf(failure: ApiError): Partial<Record<OwnProviderField, string>> {
  const named: Partial<Record<OwnProviderField, string>> = {};
  for (const field of ['issuer', 'client_id', 'audience'] as OwnProviderField[]) {
    const reason = failure.fieldError(`identity.${field}`);
    if (reason !== undefined) named[field] = reason;
  }
  return named;
}

/** The server's complaints, keyed by the box they are about. */
function fieldsOf(failure: ApiError): Errors {
  const named: Errors = {};
  const fields: Field[] = ['slug', 'display_name', 'version', 'admin_group'];
  for (const field of fields) {
    const reason = failure.fieldError(field);
    if (reason !== undefined) named[field] = reason;
  }
  return named;
}

/**
 * A taken slug and a taken domain are one refusal with one code, and the answer
 * carries nothing that says which. So the console says both rather than
 * highlighting one of them and being wrong half the time.
 */
function DeclareFailure({ failure }: { failure: ApiError }) {
  const { t } = useI18n();

  if (failure.code === 'conflict') {
    return (
      <Alert tone="warn" title={t('createTenant.taken.title')} live>
        {t('createTenant.taken.body')}
      </Alert>
    );
  }
  if (failure.code === 'provider_identity_unavailable') {
    return (
      <Alert tone="warn" title={t('createTenant.noProvider.title')} live>
        {t('createTenant.noProvider.body')}
      </Alert>
    );
  }
  return <ErrorAlert error={failure} />;
}
